import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { initAppearance } from './lib/appearance';
initAppearance();
import ErrorBoundary from './components/ErrorBoundary';
import { TenantProvider } from './TenantContext';
import { WebPhoneProvider } from './context/WebPhoneContext';
// Pocket stays eager: it's the standalone PWA and must open instantly.
import Pocket from './pages/Pocket';
import './App.css';

// ── Code-split every CRM page so /pocket (and first paint) load a tiny bundle ──
const Layout = lazy(() => import('./components/Layout'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Leads = lazy(() => import('./pages/Leads'));
const CoachHistory = lazy(() => import('./pages/CoachHistory'));
const CoachMonitor = lazy(() => import('./pages/CoachMonitor'));
const WhatsAppInbox = lazy(() => import('./pages/WhatsAppInbox'));
const Customers = lazy(() => import('./pages/Customers'));
const Banking = lazy(() => import('./pages/Banking'));
const WithdrawalRequests = lazy(() => import('./pages/WithdrawalRequests'));
const Accounts = lazy(() => import('./pages/Accounts'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Emails = lazy(() => import('./pages/Emails'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Finance = lazy(() => import('./pages/Finance'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Messages = lazy(() => import('./pages/Messages'));
const Reports = lazy(() => import('./pages/Reports'));
const TradingManager = lazy(() => import('./pages/TradingManager'));
const VoiceManagement = lazy(() => import('./pages/VoiceManagement'));
const CallRecordings = lazy(() => import('./pages/CallRecordings'));
const VoiceControlCenter = lazy(() => import('./pages/VoiceControlCenter'));
const IntegrationStatus = lazy(() => import('./pages/IntegrationStatus'));
const Notifications = lazy(() => import('./pages/Notifications'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminCommissions = lazy(() => import('./pages/AdminCommissions'));
const MyCommissions = lazy(() => import('./pages/MyCommissions'));
const MasterTenants = lazy(() => import('./pages/MasterTenants'));
const AIHelper = lazy(() => import('./pages/AIHelper'));
const HealthWatch = lazy(() => import('./pages/HealthWatch'));
const Trends = lazy(() => import('./pages/Trends'));
const CommandCenter = lazy(() => import('./pages/CommandCenter'));
const EmailCampaigns = lazy(() => import('./pages/EmailCampaigns'));
const ProductionReadiness = lazy(() => import('./pages/ProductionReadiness'));
const WhatsAppCalls = lazy(() => import('./pages/WhatsAppCalls'));
const AiTraining = lazy(() => import('./pages/AiTraining'));
const Communicator = lazy(() => import('./pages/Communicator'));
const DemoRedeem = lazy(() => import('./pages/DemoRedeem'));
const AgentsKpi = lazy(() => import('./pages/AgentsKpi'));
const AIInbox = lazy(() => import('./pages/ai').then((m) => ({ default: m.AIInbox })));
const SuperAdminLogin = lazy(() => import('./pages/superadmin').then((m) => ({ default: m.SuperAdminLogin })));
const SuperAdminDashboard = lazy(() => import('./pages/superadmin').then((m) => ({ default: m.SuperAdminDashboard })));
const TenantList = lazy(() => import('./pages/superadmin').then((m) => ({ default: m.TenantList })));
const CreateTenant = lazy(() => import('./pages/superadmin').then((m) => ({ default: m.CreateTenant })));
const TenantDetail = lazy(() => import('./pages/superadmin').then((m) => ({ default: m.TenantDetail })));

const PageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Smart index — sales agents go straight to Leads (their only allowed page);
// everyone else goes to Dashboard. Reads user from useAuth first, falls back
// to localStorage so role is available immediately after login (avoids the
// useState-batching race that lands every fresh login on /dashboard).
function SmartIndex() {
  const { user } = useAuth();
  let role = (user?.role || '').toLowerCase();
  if (!role) {
    try {
      const raw = localStorage.getItem('crm_user');
      if (raw) role = (JSON.parse(raw)?.role || '').toLowerCase();
    } catch { /* ignore */ }
  }
  if (role === 'sales' || role === 'affiliate') return <Navigate to="/crm/leads" replace />;
  return <Navigate to="/dashboard" replace />;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Role-aware redirect helper — sales/affiliate go to /crm/leads, others to /dashboard.
// Used by PublicRoute + the catch-all so newly-logged-in non-admin users don't
// get bounced into /dashboard which they may not have access to.
function roleAwareLandingPath() {
  try {
    const raw = localStorage.getItem('crm_user');
    const role = raw ? (JSON.parse(raw)?.role || '').toLowerCase() : '';
    if (role === 'sales' || role === 'affiliate') return '/crm/leads';
  } catch { /* ignore */ }
  return '/dashboard';
}

// Public Route Component (redirect to dashboard if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to={roleAwareLandingPath()} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageSpinner />}>
    <Routes>
      {/* SuperAdmin Routes (separate from tenant auth) */}
      <Route path="/superadmin" element={<SuperAdminLogin />} />
      <Route path="/demo/:token" element={<DemoRedeem />} />
      <Route path="/superadmin/dashboard" element={<SuperAdminDashboard />}>
        <Route index element={null} />
      </Route>
      <Route path="/superadmin/tenants" element={<SuperAdminDashboard />}>
        <Route index element={<TenantList />} />
        <Route path="new" element={<CreateTenant />} />
        <Route path=":id" element={<TenantDetail />} />
      </Route>
      <Route path="/superadmin/users" element={<SuperAdminDashboard />}>
        <Route index element={<div className="p-8 text-center text-slate-500">User management coming soon...</div>} />
      </Route>
      <Route path="/superadmin/announcements" element={<SuperAdminDashboard />}>
        <Route index element={<div className="p-8 text-center text-slate-500">Announcements coming soon...</div>} />
      </Route>
      <Route path="/superadmin/features" element={<SuperAdminDashboard />}>
        <Route index element={<div className="p-8 text-center text-slate-500">Feature flags coming soon...</div>} />
      </Route>
      <Route path="/superadmin/audit" element={<SuperAdminDashboard />}>
        <Route index element={<div className="p-8 text-center text-slate-500">Audit logs coming soon...</div>} />
      </Route>
      <Route path="/superadmin/settings" element={<SuperAdminDashboard />}>
        <Route index element={<div className="p-8 text-center text-slate-500">Settings coming soon...</div>} />
      </Route>

      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Pocket — standalone voice-first PWA. Renders OUTSIDE the CRM
          Layout so it has its own design, its own login, its own install
          manifest. All data still flows into the CRM finance module. */}
      <Route path="/pocket" element={<Pocket />} />
      <Route path="/pocket/*" element={<Pocket />} />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<SmartIndex />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="crm/leads" element={<Leads />} />
        <Route path="crm/coach-history" element={<CoachHistory />} />
        <Route path="crm/coach-monitor" element={<CoachMonitor />} />
        <Route path="crm/whatsapp-inbox" element={<WhatsAppInbox />} />
        <Route path="crm/customers" element={<Customers />} />
        <Route path="crm/:status" element={<Leads />} />
        <Route path="banking" element={<Banking />} />
        <Route path="withdrawals" element={<WithdrawalRequests />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="emails" element={<Emails />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="finance" element={<Finance />} />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="attendance" element={<Attendance />} />
        {/* AI Features */}
        <Route path="ai-inbox" element={<AIInbox />} />
        <Route path="messages" element={<Messages />} />
        <Route path="reports" element={<Reports />} />
        <Route path="integration-status" element={<IntegrationStatus />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/agents-kpi" element={<AgentsKpi />} />
        <Route path="admin/commissions" element={<AdminCommissions />} />
        <Route path="my-commissions" element={<MyCommissions />} />
        <Route path="master/tenants" element={<MasterTenants />} />
        <Route path="master/ai-helper" element={<AIHelper />} />
        <Route path="master/health-watch" element={<HealthWatch />} />
        <Route path="master/trends" element={<Trends />} />
        <Route path="master/command-center" element={<CommandCenter />} />
        <Route path="email-campaigns" element={<EmailCampaigns />} />
        <Route path="production-readiness" element={<ProductionReadiness />} />
        <Route path="master/whatsapp-calls" element={<WhatsAppCalls />} />
        <Route path="master/ai-training" element={<AiTraining />} />
        <Route path="trading-manager" element={<TradingManager />} />
        <Route path="voice-management" element={<VoiceManagement />} />
        <Route path="call-recordings" element={<CallRecordings />} />
        <Route path="voice-control" element={<VoiceControlCenter />} />
        <Route path="admin/communicator" element={<Communicator />} />
      </Route>

      {/* Catch all — route by role */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div>
      <ErrorBoundary>
        <AuthProvider>
          <TenantProvider>
            <WebPhoneProvider>
              <Router>
                <AppRoutes />
              </Router>
            </WebPhoneProvider>
            <Toaster
              position="top-right"
              toastOptions={{
                className: 'bg-card border-border text-foreground',
              }}
            />
          </TenantProvider>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
